import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Statistic,
  Typography,
  Row,
  Col,
  Button,
  Input,
  Select,
  Spin,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  DollarOutlined,
  RiseOutlined,
  BankOutlined,
  FileTextOutlined,
  EditOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

const { Text, Title } = Typography;

const zambianColors = {
  green: "#198754",
  orange: "#de7921",
  red: "#da291c",
  black: "#000000",
};

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

/**
 * Parse the structured notes field to extract transaction details.
 * Format: "Interest payment to {name} | Borrowed: K{amount} | Rate: {rate}% | Interest: K{amount}"
 */
const parseNotes = (notes) => {
  if (!notes) return { investorName: "", borrowed: 0, rate: 0, interest: 0 };

  const nameMatch = notes.match(/Interest payment to (.+?) \|/);
  const borrowedMatch = notes.match(/Borrowed: K([\d,.]+)/);
  const rateMatch = notes.match(/Rate: ([\d.]+)%/);
  const interestMatch = notes.match(/Interest: K([\d,.]+)/);

  return {
    investorName: nameMatch ? nameMatch[1].trim() : "",
    borrowed: borrowedMatch
      ? parseFloat(borrowedMatch[1].replace(/,/g, ""))
      : 0,
    rate: rateMatch ? parseFloat(rateMatch[1]) / 100 : 0,
    interest: interestMatch
      ? parseFloat(interestMatch[1].replace(/,/g, ""))
      : 0,
  };
};

const fmtK = (val) => {
  const num = parseFloat(val || 0);
  return `K ${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtPct = (val) => {
  const num = parseFloat(val || 0) * 100;
  return `${num.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const JutemInterestExpensesPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  const DIRECTUS_URL =
    process.env.REACT_APP_DIRECTUS_URL || "https://zedlenders.pickmesms.com";

  useEffect(() => {
    if (company?.id !== 22) {
      message.warning("This page is only for Jutem Fund (Company ID 22)");
      navigate("/");
      return;
    }
    fetchData();
  }, [company]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
        headers,
        params: {
          filter: {
            company: { _eq: 22 },
            transaction_type: { _eq: "interest_expense" },
          },
          fields:
            "id,reference_number,notes,amount,transaction_date,investment_reference",
          limit: -1,
          sort: "transaction_date",
        },
      });

      setTransactions(res.data.data || []);
    } catch (error) {
      console.error("Error fetching interest expenses:", error);
      message.error("Failed to load interest expenses");
    } finally {
      setLoading(false);
    }
  };

  // Parse all transactions into enriched rows
  const enrichedData = useMemo(() => {
    return transactions.map((txn) => {
      const parsed = parseNotes(txn.notes);
      const txnDate = txn.transaction_date
        ? moment(txn.transaction_date)
        : null;
      return {
        ...txn,
        investorName: parsed.investorName,
        borrowed: parsed.borrowed,
        rate: parsed.rate,
        paid: parsed.borrowed + parsed.interest,
        interestExpense: parseFloat(txn.amount || 0),
        txnYear: txnDate ? txnDate.year() : null,
        txnMonth: txnDate ? txnDate.month() + 1 : null,
      };
    });
  }, [transactions]);

  const availableYears = useMemo(() => {
    const years = [
      ...new Set(enrichedData.map((r) => r.txnYear).filter(Boolean)),
    ]
      .sort()
      .reverse();
    return years;
  }, [enrichedData]);

  const availableMonths = useMemo(() => {
    if (!selectedYear) return MONTHS;
    const monthsInYear = new Set(
      enrichedData
        .filter((r) => r.txnYear === selectedYear)
        .map((r) => r.txnMonth)
        .filter(Boolean)
    );
    return MONTHS.filter((m) => monthsInYear.has(m.value));
  }, [enrichedData, selectedYear]);

  const filteredData = useMemo(() => {
    let filtered = [...enrichedData];

    if (selectedYear) {
      filtered = filtered.filter((r) => r.txnYear === selectedYear);
    }

    if (selectedMonth) {
      filtered = filtered.filter((r) => r.txnMonth === selectedMonth);
    }

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((r) =>
        (r.investorName || "").toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [enrichedData, selectedYear, selectedMonth, searchText]);

  const stats = useMemo(() => {
    const totalInterest = filteredData.reduce(
      (s, r) => s + r.interestExpense,
      0
    );
    const totalBorrowed = filteredData.reduce((s, r) => s + r.borrowed, 0);
    const totalPaid = filteredData.reduce((s, r) => s + r.paid, 0);
    return {
      totalInterest,
      totalBorrowed,
      totalPaid,
      count: filteredData.length,
    };
  }, [filteredData]);

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      amount: record.interestExpense,
      transaction_date: record.transaction_date ? moment(record.transaction_date) : null,
      borrowed: record.borrowed,
      rate: record.rate * 100,
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Rebuild the notes string with updated values
      const newNotes = `Interest payment to ${editingRecord.investorName} | Borrowed: K${values.borrowed.toLocaleString()} | Rate: ${values.rate}% | Interest: K${values.amount.toLocaleString()}`;

      await axios.patch(
        `${DIRECTUS_URL}/items/transactions/${editingRecord.id}`,
        {
          amount: values.amount,
          transaction_date: values.transaction_date
            ? values.transaction_date.format("YYYY-MM-DD")
            : null,
          notes: newNotes,
        },
        { headers }
      );

      message.success("Interest expense updated successfully");
      setEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();
      fetchData();
    } catch (error) {
      console.error("Error updating interest expense:", error);
      message.error("Failed to update interest expense");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((r, i) => ({
      "#": i + 1,
      Name: r.investorName,
      "Date Expected": r.transaction_date
        ? moment(r.transaction_date).format("DD/MM/YYYY")
        : "",
      "Borrowed (ZMW)": r.borrowed.toFixed(2),
      "Interest Rate": fmtPct(r.rate),
      "Paid (ZMW)": r.paid.toFixed(2),
      "Interest Expense (ZMW)": r.interestExpense.toFixed(2),
    }));

    const monthLabel = selectedMonth
      ? MONTHS.find((m) => m.value === selectedMonth)?.label || ""
      : "All";
    const yearLabel = selectedYear || "All";

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Interest Expenses");
    XLSX.writeFile(
      wb,
      `Jutem_Interest_Expenses_${monthLabel}_${yearLabel}.xlsx`
    );
    message.success("Exported successfully");
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
      align: "center",
    },
    {
      title: "Name",
      key: "investorName",
      dataIndex: "investorName",
      sorter: (a, b) => (a.investorName || "").localeCompare(b.investorName || ""),
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Date Expected",
      key: "transaction_date",
      dataIndex: "transaction_date",
      render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) =>
        (a.transaction_date || "").localeCompare(b.transaction_date || ""),
    },
    {
      title: "Borrowed",
      key: "borrowed",
      dataIndex: "borrowed",
      render: (v) => fmtK(v),
      sorter: (a, b) => a.borrowed - b.borrowed,
      align: "right",
    },
    {
      title: "Interest Rate",
      key: "rate",
      dataIndex: "rate",
      render: (v) => fmtPct(v),
      sorter: (a, b) => a.rate - b.rate,
      align: "center",
    },
    {
      title: "Paid",
      key: "paid",
      dataIndex: "paid",
      render: (v) => fmtK(v),
      sorter: (a, b) => a.paid - b.paid,
      align: "right",
    },
    {
      title: "Interest Expense",
      key: "interestExpense",
      dataIndex: "interestExpense",
      render: (v) => (
        <Text style={{ color: zambianColors.red, fontWeight: "bold" }}>
          {fmtK(v)}
        </Text>
      ),
      sorter: (a, b) => a.interestExpense - b.interestExpense,
      align: "right",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Interest Expenses...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <RiseOutlined
          style={{ marginRight: 12, color: zambianColors.red }}
        />
        Interest Expenses
      </Title>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.red,
              boxShadow: "0 2px 8px rgba(218, 41, 28, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span
                  style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}
                >
                  Total Interest Expense
                </span>
              }
              value={stats.totalInterest}
              precision={2}
              prefix={
                <DollarOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={
                <span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.orange,
              boxShadow: "0 2px 8px rgba(222, 121, 33, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span
                  style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}
                >
                  Total Borrowed
                </span>
              }
              value={stats.totalBorrowed}
              precision={2}
              prefix={
                <BankOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={
                <span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(25, 135, 84, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span
                  style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}
                >
                  Total Paid
                </span>
              }
              value={stats.totalPaid}
              precision={2}
              prefix={
                <DollarOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={
                <span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: "#1890ff",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span
                  style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}
                >
                  Transactions
                </span>
              }
              value={stats.count}
              prefix={
                <FileTextOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by investor name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={(v) => {
                setSelectedYear(v);
                setSelectedMonth(null);
              }}
              allowClear
              style={{ width: "100%" }}
            >
              {availableYears.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              allowClear
              style={{ width: "100%" }}
            >
              {availableMonths.map((m) => (
                <Select.Option key={m.value} value={m.value}>
                  {m.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={4} md={4}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              block
              style={{
                background: zambianColors.green,
                borderColor: zambianColors.green,
              }}
            >
              Export
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100"],
            showTotal: (total) => `${total} transactions`,
          }}
          scroll={{ x: true }}
          size="small"
          summary={() => {
            const totalBorrowed = filteredData.reduce(
              (s, r) => s + r.borrowed,
              0
            );
            const totalPaid = filteredData.reduce((s, r) => s + r.paid, 0);
            const totalInterest = filteredData.reduce(
              (s, r) => s + r.interestExpense,
              0
            );

            return (
              <Table.Summary fixed>
                <Table.Summary.Row
                  style={{ fontWeight: "bold", backgroundColor: "#fafafa" }}
                >
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>
                    <Text strong>TOTAL</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {filteredData.length} entries
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{fmtK(totalBorrowed)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>{fmtK(totalPaid)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text
                      strong
                      style={{ color: zambianColors.red }}
                    >
                      {fmtK(totalInterest)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Edit Interest Expense Modal */}
      <Modal
        title="Edit Interest Expense"
        open={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="transaction_date"
            label="Date Expected"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="borrowed"
            label="Borrowed Amount (ZMW)"
            rules={[{ required: true, message: "Please enter borrowed amount" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/,/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="rate"
            label="Interest Rate (%)"
            rules={[{ required: true, message: "Please enter interest rate" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} max={100} precision={1} />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Interest Expense (ZMW)"
            rules={[{ required: true, message: "Please enter interest expense" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/,/g, "")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default JutemInterestExpensesPage;
